import {
    getCommercialUsage,
    preflightCommercialAction,
} from "../Services/commercialControl.service";
import { CommercialControlActionTypes } from "./actionTypes";

const GENERIC_COMMERCIAL_ERROR_CODE = "commercial_control_error";

export const getCommercialUsageIdentityKey = (user = {}) => {
    if (!user || typeof user !== "object") return null;

    const userId = user.id || user.user_id || null;
    if (!userId) return null;

    const workspaceId =
        user.workspace_id ||
        user.client_id ||
        user.client?.id ||
        user.client?.uuid ||
        user.client_name ||
        "workspace";

    return `${workspaceId}:${userId}`;
};

export const selectCommercialUsageIdentityKey = (state = {}) => {
    const usersState = state.users || {};
    const activeUser = usersState.user?.id ? usersState.user : usersState.currentUser;
    return getCommercialUsageIdentityKey(activeUser);
};

const unwrapSuccessResponseData = (response) => {
    if (response && typeof response === "object" && Object.prototype.hasOwnProperty.call(response, "data")) {
        return response.data;
    }
    return response;
};

export const isBlockedCommercialDecision = (decision) =>
    decision?.status === "blocked" ||
    (decision?.allowed === false && decision?.status !== "requires_confirmation");

export const isPartialAnalysisCommercialDecision = (decision) =>
    decision?.status === "requires_confirmation";

export const buildPartialAnalysisRetryContext = (decision, launchContext = {}) => {
    if (!isPartialAnalysisCommercialDecision(decision)) return null;
    if (!launchContext.criteriaToSend || !launchContext.documentVersionId) return null;

    return {
        criteriaToSend: launchContext.criteriaToSend,
        documentVersionId: launchContext.documentVersionId,
        gateCode: decision.code,
        ...(launchContext.reviewMode ? { reviewMode: launchContext.reviewMode } : {}),
    };
};

export const normalizeCommercialDecision = (responseOrError) => {
    const raw = responseOrError?.response?.data || responseOrError || {};
    const candidate = raw?.data || raw?.detail || raw;

    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
        const nested = candidate?.data && typeof candidate.data === "object"
            ? candidate.data
            : candidate;

        if (
            Object.prototype.hasOwnProperty.call(nested, "allowed") ||
            Object.prototype.hasOwnProperty.call(nested, "status") ||
            Object.prototype.hasOwnProperty.call(nested, "code") ||
            (nested.message && typeof nested.message === "object" && !Array.isArray(nested.message))
        ) {
            const hasDecisionCode = Object.prototype.hasOwnProperty.call(nested, "code");
            return {
                allowed: Boolean(nested.allowed),
                status: nested.status || "error",
                code: hasDecisionCode ? nested.code : (raw.code || GENERIC_COMMERCIAL_ERROR_CODE),
                message: nested.message || null,
                metadata_json: nested.metadata_json || {},
                usage_snapshot: nested.usage_snapshot || null,
                action: nested.action || raw.action || null,
            };
        }
    }

    const fallbackMessage =
        raw?.message ||
        raw?.error ||
        (typeof raw?.detail === "string" ? raw.detail : null) ||
        responseOrError?.message ||
        "Commercial control unavailable.";

    return {
        allowed: false,
        status: "error",
        code: raw?.code || GENERIC_COMMERCIAL_ERROR_CODE,
        message: {
            code: raw?.code || GENERIC_COMMERCIAL_ERROR_CODE,
            title_key: "commercial.error.title",
            body_key: "commercial.error.body",
            default_title_fr: "Controle commercial indisponible",
            default_body_fr: fallbackMessage,
            default_title_en: "Commercial control unavailable",
            default_body_en: fallbackMessage,
            severity: "blocked",
            source: "frontend_fallback",
        },
        metadata_json: {},
        usage_snapshot: null,
        action: raw?.action || null,
    };
};

const isCurrentCommercialUsageRequest = (getState, identityKey) => {
    if (!identityKey) return true;
    return selectCommercialUsageIdentityKey(getState()) === identityKey;
};

export const fetchCommercialUsage = () => async (dispatch, getState = () => ({})) => {
    const identityKey = selectCommercialUsageIdentityKey(getState());
    const meta = { identityKey };

    dispatch({ type: CommercialControlActionTypes.COMMERCIAL_USAGE_REQUEST, meta });
    try {
        const response = await getCommercialUsage();
        const usage = unwrapSuccessResponseData(response);
        if (!isCurrentCommercialUsageRequest(getState, identityKey)) {
            return null;
        }
        dispatch({
            type: CommercialControlActionTypes.COMMERCIAL_USAGE_SUCCESS,
            payload: usage,
            meta,
        });
        return usage;
    } catch (error) {
        if (!isCurrentCommercialUsageRequest(getState, identityKey)) {
            return null;
        }
        const decision = normalizeCommercialDecision(error);
        dispatch({
            type: CommercialControlActionTypes.COMMERCIAL_USAGE_FAILURE,
            payload: decision,
            meta,
        });
        throw error;
    }
};

export const runCommercialPreflight = (payload) => async (dispatch) => {
    dispatch({ type: CommercialControlActionTypes.COMMERCIAL_PREFLIGHT_REQUEST });
    try {
        const response = await preflightCommercialAction(payload);
        const decision = normalizeCommercialDecision(unwrapSuccessResponseData(response));
        dispatch({
            type: CommercialControlActionTypes.COMMERCIAL_PREFLIGHT_SUCCESS,
            payload: decision,
        });
        return decision;
    } catch (error) {
        const decision = normalizeCommercialDecision(error);
        dispatch({
            type: CommercialControlActionTypes.COMMERCIAL_PREFLIGHT_FAILURE,
            payload: decision,
        });
        throw error;
    }
};

export const runCommercialPreflightLaunchGate = (
    payload,
    {
        launch,
        onBlocked,
        onRequiresConfirmation,
        onFailure,
    } = {}
) => async (dispatch) => {
    let decision;
    try {
        decision = await dispatch(runCommercialPreflight(payload));
    } catch (error) {
        const normalized = normalizeCommercialDecision(error);
        if (typeof onFailure === "function") {
            onFailure(normalized, error);
        }
        return { status: "failure", decision: normalized, error };
    }

    if (isPartialAnalysisCommercialDecision(decision)) {
        if (typeof onRequiresConfirmation === "function") {
            onRequiresConfirmation(decision);
        }
        return { status: "requires_confirmation", decision };
    }

    if (isBlockedCommercialDecision(decision)) {
        if (typeof onBlocked === "function") {
            onBlocked(decision);
        }
        return { status: "blocked", decision };
    }

    if (typeof launch === "function") {
        launch(decision);
    }
    return { status: "launched", decision };
};

export const setActiveReservationId = (reservationId) => ({
    type: CommercialControlActionTypes.COMMERCIAL_SET_ACTIVE_RESERVATION,
    payload: reservationId || null,
});

export const clearActiveReservationId = () => ({
    type: CommercialControlActionTypes.COMMERCIAL_CLEAR_ACTIVE_RESERVATION,
});

export const openCommercialGateModal = (decision) => ({
    type: CommercialControlActionTypes.COMMERCIAL_OPEN_GATE_MODAL,
    payload: decision || null,
});

export const closeCommercialGateModal = () => ({
    type: CommercialControlActionTypes.COMMERCIAL_CLOSE_GATE_MODAL,
});

export const setPartialAnalysisDecision = (decision) => ({
    type: CommercialControlActionTypes.COMMERCIAL_SET_PARTIAL_ANALYSIS_DECISION,
    payload: decision || null,
});

export const clearPartialAnalysisDecision = () => ({
    type: CommercialControlActionTypes.COMMERCIAL_CLEAR_PARTIAL_ANALYSIS_DECISION,
});

export const clearCommercialControl = () => ({
    type: CommercialControlActionTypes.COMMERCIAL_RESET,
});
